import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MetadataField } from "@/shared/lib/forms";

/**
 * Shown under the customer email whenever one is typed. Ticked says the email
 * is not the customer's: it is kept as the registrant's and the customer gets
 * a record of their own. Auto-ticked when the lookup says it is the
 * registrant's own (their login, or their company's).
 */
export function RegistrantEmailTick({
  email,
  isRegistrantEmail,
  emailIsOwners,
  onChange,
}: {
  email: string;
  isRegistrantEmail: boolean;
  emailIsOwners: boolean | null;
  onChange: (emailIsOwners: boolean | null) => void;
}) {
  const { t } = useTranslation();

  // Stored, not derived, so the review step and the payload agree.
  useEffect(() => {
    if (isRegistrantEmail && emailIsOwners === null) onChange(false);
  }, [isRegistrantEmail, emailIsOwners, onChange]);

  if (!email.trim()) return null;
  const ticked = emailIsOwners === false;
  return (
    <div className="mt-2" data-testid="registrant-email-tick">
      <MetadataField
        field={{
          code_name: "registrantEmail",
          display_name: t(
            "registrations.customer.registrantEmail",
            "Not the customer's email",
          ),
          field_type: "BOOLEAN",
          required: false,
          description: ticked
            ? t(
                "registrations.customer.registrantEmailHint",
                "Kept as the registrant's email; it gets a copy of the {{holdingNounLower}} documents. The customer gets a record of their own.",
              )
            : undefined,
        }}
        value={ticked}
        // Unticking an auto-tick says it really is the customer's; unticking
        // a manual one lets the owner rule decide again.
        onChange={(v) => onChange(v ? false : isRegistrantEmail ? true : null)}
      />
    </div>
  );
}
